import { useUnit } from 'effector-react';
import { Eye, ListOrdered, Mail } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { $locale, localeChanged } from '../../entities/preferences';
import { SignInCard } from '../../features/sign-in';
import { isLocale } from '../../shared/lib/locale';
import { KineticDots, Mark, Pill, Segmented, Wordmark } from '../../shared/ui';
import { PositionCard } from '../../widgets/position-card';
import { EXAMPLE } from './example';

/** Research links from the original Botimir homepage, kept in the footer. */
const LINKS = [
  { label: 'Finviz', href: 'https://finviz.com' },
  { label: 'Yahoo Finance', href: 'https://finance.yahoo.com' },
  { label: 'Google Finance', href: 'https://www.google.com/finance' },
] as const;

/**
 * What a signed-out visitor sees, on any URL: what Botimir does, a real position card as the
 * illustration, and sign-in. After signing in, the URL they asked for opens.
 */
export function HomePage() {
  const { t } = useTranslation();
  return (
    <div className="relative h-full overflow-y-auto bg-bg-0">
      <KineticDots />
      <div className="relative flex min-h-full flex-col">
        <header className="mx-auto flex h-14 w-full max-w-6xl items-center justify-between px-6 max-sm:px-4">
          <span className="flex items-center gap-2">
            <Mark />
            <Wordmark />
          </span>
          <LanguageSwitch />
        </header>

        <main className="mx-auto w-full max-w-6xl flex-1 px-6 max-sm:px-4">
          {/* Wide: intro and example on the left, sign-in on the right. Narrow: the left column
              dissolves (display: contents) so sign-in comes right after the intro. */}
          <section className="flex flex-col gap-10 py-14 lg:flex-row lg:items-start lg:justify-between lg:gap-16 lg:py-24">
            <div className="min-w-0 flex-1 max-lg:contents">
              <div className="max-lg:order-1">
                <Pill>{t('home.eyebrow')}</Pill>
                <h1 className="mt-5 text-display-sm font-semibold sm:text-display">
                  <span className="block text-text-1">{t('home.headline')}</span>
                  <span className="block text-text-3">{t('home.headline2')}</span>
                </h1>
                <p className="mt-5 max-w-xl text-base text-text-2">{t('home.lead')}</p>
              </div>
              <figure className="max-w-2xl max-lg:order-3 lg:mt-10">
                <div inert aria-hidden>
                  <PositionCard
                    position={EXAMPLE}
                    selected={false}
                    onSelect={() => undefined}
                    onOpen={() => undefined}
                  />
                </div>
                <figcaption className="mt-2 text-xs text-text-3">{t('home.example')}</figcaption>
              </figure>
            </div>
            <SignInCard className="w-[min(360px,calc(100vw-32px))] shrink-0 max-lg:order-2 max-lg:w-full" />
          </section>

          <section aria-labelledby="home-steps" className="pb-16">
            <h2 id="home-steps" className="sr-only">
              {t('home.stepsTitle')}
            </h2>
            <ol className="grid gap-3 md:grid-cols-3">
              <Step icon={<ListOrdered />} n={1} title={t('home.plan')} body={t('home.planBody')} />
              <Step icon={<Eye />} n={2} title={t('home.watch')} body={t('home.watchBody')} />
              <Step icon={<Mail />} n={3} title={t('home.act')} body={t('home.actBody')} />
            </ol>
          </section>
        </main>

        <Footer />
      </div>
    </div>
  );
}

function Step({
  icon,
  n,
  title,
  body,
}: {
  icon: ReactNode;
  n: number;
  title: string;
  body: string;
}) {
  return (
    <li className="rounded-lg border border-border-subtle bg-bg-1 px-4 py-3">
      <div className="flex items-center gap-2 text-text-3 [&_svg]:size-4">
        {icon}
        <span className="tabular text-xs">{String(n).padStart(2, '0')}</span>
      </div>
      <h3 className="mt-2 text-sm font-medium">{title}</h3>
      <p className="mt-1 text-sm text-text-3">{body}</p>
    </li>
  );
}

function LanguageSwitch() {
  const { t } = useTranslation();
  const [locale, change] = useUnit([$locale, localeChanged]);
  return (
    <Segmented
      label={t('preferences.language')}
      value={locale}
      onValueChange={(value) => {
        if (isLocale(value)) change(value);
      }}
      options={[
        { value: 'en', label: 'EN' },
        { value: 'bg', label: 'БГ' },
      ]}
    />
  );
}

function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="px-4 py-8 text-center text-xs text-text-3">
      <nav aria-label={t('home.links')} className="inline">
        {LINKS.map((link, index) => (
          <Fragment key={link.href}>
            {index > 0 && (
              <span aria-hidden className="px-2 text-text-4">
                |
              </span>
            )}
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-sm text-text-2 outline-none hover:text-text-1 focus-visible:ring-2 focus-visible:ring-focus"
            >
              {link.label}
            </a>
          </Fragment>
        ))}
      </nav>
      <span className="px-2 text-text-4" aria-hidden>
        ·
      </span>
      <span>{t('home.copyright', { year: new Date().getFullYear() })}</span>
    </footer>
  );
}
